import { z } from 'zod'
import type { Tool } from '../types.js'
import type { InlineEditResult, ProposedEditSummary, ProposeEditDeps } from './propose-edit.js'

/**
 * propose_chord_change — music-domain wrapper over `propose_edit` for the
 * chord advisor.
 *
 * The advisor calls this with a stable chord block id and the desired
 * chord (root + optional variant + optional bassNote). We emit an
 * `editor-command` AgentPatch keyed on `chord.replace` so the apply path
 * runs the same plugin-registered applicator regardless of which tool
 * authored the proposal.
 *
 * Variant tokens MUST be ASCII (`maj7`, `m7`, `m7b5`, `aug`, `sus4`,
 * `dim7`) — glyphs like `△7` / `ø7` are render-only and never go into
 * patch args. Bowie's call; non-negotiable for v1.
 *
 * `voiceLeading` is intentionally optional and is NOT forwarded into the
 * command args. It exists so the model can show its work in the
 * proposal's reasoning, but `chord.replace` only acts on
 * (root, variant?, bassNote?).
 */
const proposeChordChangeSchema = z.object({
  chordBlockId: z
    .string()
    .min(1)
    .describe(
      'Stable id of the chord block to replace. Defaults to the focus block id when omitted and the turn has anchor context.'
    )
    .optional(),
  root: z.string().min(1).describe('New chord root, e.g., "C", "F#", "Bb". Spell per the prevailing key signature.'),
  variant: z
    .string()
    .describe(
      'ASCII variant token: "" (major triad), "m", "7", "m7", "maj7", "m7b5", "aug", "dim", "sus4", "dim7", etc.'
    )
    .optional(),
  bassNote: z.string().describe('Optional slash bass note, e.g., "E" for C/E.').optional(),
  voiceLeading: z
    .string()
    .describe('Optional reasoning about voice-leading. Not consumed by the command; for the proposal summary only.')
    .optional(),
  summary: z
    .string()
    .min(1)
    .describe(
      'One sentence saying WHY this change helps, shown to the user on the proposal chip and the ' +
        'inline ghost, e.g., "Swap C for Am7 to lean into the relative minor".'
    ),
})

export const createProposeChordChangeTool = (
  deps: ProposeEditDeps,
  opts: { inline?: boolean } = {}
): Tool<z.infer<typeof proposeChordChangeSchema>, ProposedEditSummary | InlineEditResult> => ({
  name: 'propose_chord_change',
  description:
    'Propose replacing one chord in the current song with a different chord. Anchors to a chord block id; defaults to the focus block when the turn has anchor context. The user accepts or rejects in the gutter. Prefer this over propose_edit / run_editor_command for chord substitutions.',
  schema: proposeChordChangeSchema,
  handler: async (args, ctx) => {
    const blockId = args.chordBlockId ?? ctx.focus?.id
    if (!blockId) {
      return {
        ok: false,
        error: 'invalid_arguments',
        invalidArguments: {
          tool: 'propose_chord_change',
          issues: [
            {
              path: 'chordBlockId',
              message:
                'chordBlockId is required when the turn has no focus. Pass chordBlockId explicitly or invoke from a block-anchored ask.',
            },
          ],
          recovery:
            `Your last call to propose_chord_change was missing the chord block anchor. ` +
            `Re-call the tool with an explicit chordBlockId pulled from the entity outline, ` +
            `or ask the user a short clarifying question if the target chord is genuinely ambiguous. ` +
            `Do not surface this validation error to the user as a chat message.`,
        },
      }
    }

    const commandArgs: Record<string, unknown> = { root: args.root }
    if (args.variant !== undefined) {
      commandArgs.variant = args.variant
    }
    if (args.bassNote !== undefined) {
      commandArgs.bassNote = args.bassNote
    }

    const patch = {
      kind: 'editor-command' as const,
      commandId: 'chord.replace',
      args: commandArgs,
      anchor: { id: blockId, position: 'replace' as const },
    }

    if (opts.inline) {
      return { ok: true, result: { kind: 'inline-edit', summary: args.summary, patch } satisfies InlineEditResult }
    }

    const proposal = await deps.createProposal({
      agentId: ctx.agentId,
      entityId: ctx.parentEntityId,
      userId: ctx.userId,
      summary: args.summary,
      patch,
    })
    return { ok: true, result: proposal }
  },
})
